import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ServicesSection = ({ services = [], typography = {} }) => {
  const navigate = useNavigate();

  const handleServiceClick = (path) => navigate(path);

  // Early return if no services are provided
  if (!services || services.length === 0) {
    return (
      <section className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">No Services Available</h2>
          <p className="text-gray-600">Services data is not available at the moment.</p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="services"
      className="relative py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-slate-50 via-white to-gray-50"
    >
      {/* Modern Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gray-200/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gray-300/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gray-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Enhanced Heading */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Our Service Offerings
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-6 rounded-full"></div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            Professional solutions designed to meet your academic and business requirements
          </motion.p>
        </div>

        {/* Modern Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <motion.div
              key={service.id || index}
              className="group relative"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              onClick={() => handleServiceClick(service.path)}
            >
              {/* Main Card */}
              <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm group-hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden">
                
                {/* Content */}
                <div className="relative p-8 h-full flex flex-col">
                  
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-700 transition-colors duration-300">
                    {service.title || 'Service Title'}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                    {service.description || 'Service description not available.'}
                  </p>

                  {/* Features */}
                  <div className="mb-8 space-y-3">
                    {(service.features || []).map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-center"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: (index * 0.1) + (idx * 0.1) }}
                        viewport={{ once: true }}
                      >
                        <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                        <span className="text-gray-700">
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  {/* CTA Button with Gradient */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="relative w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 px-6 rounded-xl font-semibold flex items-center justify-center group/button overflow-hidden transition-all duration-300 hover:shadow-lg"
                  >
                    {/* Button Content */}
                    <div className="relative flex items-center">
                      <span className="mr-2">{service.buttonText || 'Learn More'}</span>
                      <ArrowRight className="w-5 h-5 group-hover/button:translate-x-1 transition-transform duration-300" />
                    </div>
                    
                    {/* Shine Effect on Hover */}
                    <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover/button:opacity-100 transition-opacity duration-500 group-hover/button:animate-shine"></div>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes shine {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(200%) skewX(-12deg);
          }
        }
        
        .animate-shine {
          animation: shine 0.8s ease-out;
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;